import Joi from 'joi';

import { PaymentChannelEnum } from '../../enums/payment-channel.enum';
import { PaymentMethodEnum } from '../../enums/payment-method.enum';
import { PaymentProviderEnum } from '../../enums/payment-provider.enum';
import { PaymentTargetEnum } from '../../enums/payment-target.enum';

import { reservationInfoSchema } from './reservation-info.schema';

/**
 * Schema for payment initialization validation.
 */
export const initializePaymentSchema = () =>
  Joi.object({
    transactionId: Joi.number().integer().positive().allow(null).optional(),
    type: Joi.string().allow(null).optional(),
    provider: Joi.alternatives()
      .conditional(Joi.any().valid(null), {
        then: Joi.invalid(null).messages({
          'any.invalid': 'validations|payment-provider|cannot-be-null',
        }),
        otherwise: Joi.string()
          .valid(...Object.values(PaymentProviderEnum))
          .required()
          .messages({
            'any.required': 'validations|payment-provider|required',
            'any.only': 'validations|payment-provider|invalid-value',
          }),
      })
      .required()
      .messages({
        'any.required': 'validations|payment-provider|required',
      }),
    paymentMethod: Joi.alternatives()
      .conditional(Joi.any().valid(null), {
        then: Joi.invalid(null).messages({
          'any.invalid': 'validations|payment-method|cannot-be-null',
        }),
        otherwise: Joi.string()
          .valid(...Object.values(PaymentMethodEnum))
          .required()
          .messages({
            'any.required': 'validations|payment-method|required',
            'any.only': 'validations|payment-method|invalid-value',
          }),
      })
      .required()
      .messages({
        'any.required': 'validations|payment-method|required',
      }),
    channel: Joi.alternatives()
      .conditional(Joi.any().valid(null), {
        then: Joi.invalid(null).messages({
          'any.invalid': 'validations|payment-channel|cannot-be-null',
        }),
        otherwise: Joi.string()
          .valid(...Object.values(PaymentChannelEnum))
          .required()
          .messages({
            'any.required': 'validations|payment-channel|required',
            'any.only': 'validations|payment-channel|invalid-value',
          }),
      })
      .required()
      .messages({
        'any.required': 'validations|payment-channel|required',
      }),
    externalReference: Joi.string().allow(null).optional(),
    target: Joi.alternatives()
      .conditional(Joi.any().valid(null), {
        then: Joi.invalid(null).messages({
          'any.invalid': 'validations|payment-target|cannot-be-null',
        }),
        otherwise: Joi.string()
          .valid(...Object.values(PaymentTargetEnum))
          .required()
          .messages({
            'any.required': 'validations|payment-target|required',
            'any.only': 'validations|payment-target|invalid-value',
          }),
      })
      .required()
      .messages({
        'any.required': 'validations|payment-target|required',
      }),
    targetId: Joi.number().integer().positive().allow(null).optional(),
    reference: Joi.string().allow(null).optional(),
    description: Joi.when('provider', {
      is: PaymentProviderEnum.SAFERPAY,
      then: Joi.string().trim().min(1).max(1000).required(),
      otherwise: Joi.string().trim().max(1000).allow(null, ''),
    }),
    reservationInfo: Joi.when('target', {
      is: PaymentTargetEnum.RENTAL_RESERVATION,
      then: reservationInfoSchema.required(),
      otherwise: Joi.forbidden(),
    }),
    amount: Joi.when('reservationInfo', {
      is: Joi.exist(),
      then: Joi.forbidden(),
      otherwise: Joi.number().positive().required(),
    }),
    retryCount: Joi.number().integer().min(0).allow(null).optional(),
    metadata: Joi.object().allow(null).optional(),
  }).unknown(true);
